import React, { ReactNode, useEffect } from "react";
import classNames from "classnames";
import styles from "./Modal.module.css";
import "../../styles/animations.css";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  icon?: ReactNode;
  variant?: "default" | "outlined" | "filled";
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  variant = "default",
  icon,
}) => {
  const modalClass = classNames(
    styles.modal,
    {
      [styles.modalOutlined]: variant === "outlined",
      [styles.modalFilled]: variant === "filled",
    },
    "bounceInUp"
  );

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    } else {
      document.removeEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={modalClass} role="document">
        {icon && <div className={styles.modalIcon}>{icon}</div>}
        <div className={styles.modalHeader}>
          <p id="modal-title" className={styles.modalTitle}>
            {title}
          </p>
        </div>
        <div className={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
};
